import { ApiRequestError, createApiClient, type AuthResponse } from '@war-game/contracts';
import { STANDALONE } from '../platform';
import { useAuth } from '../store/auth';

/** Fingerprint simples do navegador (sinal de multi-conta; só alerta o admin). */
function fingerprint(): string {
  const key = 'wg-fp';
  let fp = localStorage.getItem(key);
  if (!fp) {
    fp = `${navigator.userAgent}|${navigator.language}|${screen.width}x${screen.height}|${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    let h = 0;
    for (let i = 0; i < fp.length; i++) h = (Math.imul(31, h) + fp.charCodeAt(i)) | 0;
    fp = `${(h >>> 0).toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
    localStorage.setItem(key, fp);
  }
  return fp;
}

const fetchWithSignals: typeof fetch = (input, init) => {
  const headers = new Headers(init?.headers);
  headers.set('x-client-fingerprint', fingerprint());
  return fetch(input, { ...init, headers });
};

let refreshing: Promise<boolean> | null = null;

/** Renova o access token com o refresh token do cookie (rotação a cada uso). */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        useAuth.getState().clear();
        return false;
      }
      const body = (await res.json()) as AuthResponse;
      useAuth.getState().setSession(body.accessToken, body.user);
      return true;
    } catch {
      useAuth.getState().clear();
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

const http = createApiClient({
  getToken: () => useAuth.getState().token,
  onUnauthorized: refreshSession,
  fetch: fetchWithSignals,
});

/** Standalone: o mesmo `call`, atendido pelo servidor local num Web Worker (carregado só nesse build). */
const local: Pick<typeof http, 'call'> = {
  call: (async (name, args) => (await import('./local')).localClient().call(name, args)) as typeof http.call,
};

export const api: Pick<typeof http, 'call'> = STANDALONE ? local : http;

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.error.message;
  if (error instanceof Error) return error.message;
  return 'Erro inesperado.';
}

export { ApiRequestError };
